
import { defineComponent, ref, onMounted, watch } from "vue"
import { Actions } from "@/store/enums/StoreEnums"
import { useStore } from "vuex"
import { useRoute, useRouter } from "vue-router"
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb"
import { ElMessage } from "element-plus"
import type { ElForm } from "element-plus"

type FormInstance = InstanceType<typeof ElForm>;

interface CouponForm {
  name: string;
  coupon_type: string;
  discount_value: number;
  min_amount: number;
  max_discount: number | null;
  box_info_ids: number[];
  allowed_times: number[];
  total_quantity: number | null;
  per_user_limit: number | null;
  start_time: string | null;
  end_time: string | null;
  validity_days: number | null;
  status: string;
  allow_user_claim: boolean;
}

export default defineComponent({
  setup() {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    const formRef = ref<FormInstance>()

    const isNew = !route.params.id

    onMounted(() => {
      if (isNew) {
        setCurrentPageBreadcrumbs("新建抽赏优惠券", ["抽赏优惠券管理;lottery_coupons"])
      } else {
        setCurrentPageBreadcrumbs("编辑抽赏优惠券", ["抽赏优惠券管理;lottery_coupons"])
      }
      loadBoxInfos()
    })

    const coupon = ref<CouponForm>({
      name: '',
      coupon_type: 'fixed',
      discount_value: 10,
      min_amount: 0,
      max_discount: null,
      box_info_ids: [],
      allowed_times: [],
      total_quantity: null,
      per_user_limit: null,
      start_time: null,
      end_time: null,
      validity_days: null,
      status: 'active',
      allow_user_claim: true
    })

    const validityType = ref<string>('forever')
    const timeRange = ref<[string, string] | null>(null)
    const boxInfoOptions = ref<Array<{ id: number; name: string }>>([])
    const boxInfoLoading = ref(false)

    // 远程搜索套系
    const searchBoxInfos = (query: string) => {
      if (query) {
        boxInfoLoading.value = true
        store
          .dispatch(Actions.GET_LOTTERY_COUPON_BOX_INFOS, { name: query })
          .then(() => {
            const searchResults = store.getters.currentLotteryCouponBoxInfos
            // 保留已选中的选项，避免搜索时丢失
            const selectedIds = new Set(coupon.value.box_info_ids)
            const selectedItems = boxInfoOptions.value.filter(item => selectedIds.has(item.id))
            // 合并搜索结果和已选项目（去重）
            const resultIds = new Set(searchResults.map((item: { id: number }) => item.id))
            const mergedOptions = [...searchResults]
            selectedItems.forEach(item => {
              if (!resultIds.has(item.id)) {
                mergedOptions.push(item)
              }
            })
            boxInfoOptions.value = mergedOptions
            boxInfoLoading.value = false
          })
          .catch(() => {
            boxInfoLoading.value = false
          })
      } else {
        // 清空搜索时，重新加载全部（同时保留已选项目）
        const selectedIds = new Set(coupon.value.box_info_ids)
        const selectedItems = boxInfoOptions.value.filter(item => selectedIds.has(item.id))
        store
          .dispatch(Actions.GET_LOTTERY_COUPON_BOX_INFOS)
          .then(() => {
            const allResults = store.getters.currentLotteryCouponBoxInfos
            const resultIds = new Set(allResults.map((item: { id: number }) => item.id))
            const mergedOptions = [...allResults]
            selectedItems.forEach(item => {
              if (!resultIds.has(item.id)) {
                mergedOptions.push(item)
              }
            })
            boxInfoOptions.value = mergedOptions
          })
      }
    }

    // 监听 validityType 变化，清空相关字段
    watch(validityType, (val) => {
      if (val === 'forever') {
        coupon.value.start_time = null
        coupon.value.end_time = null
        coupon.value.validity_days = null
        timeRange.value = null
      } else if (val === 'range') {
        coupon.value.validity_days = null
      } else if (val === 'days') {
        coupon.value.start_time = null
        coupon.value.end_time = null
        timeRange.value = null
      }
    })

    // 监听 timeRange 变化，同步到 coupon
    watch(timeRange, (val) => {
      if (val && val.length === 2) {
        coupon.value.start_time = val[0]
        coupon.value.end_time = val[1]
      } else {
        coupon.value.start_time = null
        coupon.value.end_time = null
      }
    })

    const rules = ref({
      name: [
        { required: true, message: '请输入优惠券名称', trigger: 'blur' },
        { min: 1, max: 50, message: '名称长度需在 1-50 之间', trigger: 'blur' }
      ],
      coupon_type: [
        { required: true, message: '请选择优惠券类型', trigger: 'change' }
      ],
      discount_value: [
        {
          validator: (rule: any, value: any, callback: any) => {
            if (value === undefined || value === null || value <= 0) {
              callback(new Error('请输入有效的优惠值'))
              return
            }
            if (coupon.value.coupon_type === 'percent' && (value < 1 || value > 99)) {
              callback(new Error('折扣比例需在 1-99 之间'))
              return
            }
            callback()
          },
          trigger: ['blur', 'change']
        }
      ]
    })

    // 加载套系选项（保留已选项目，避免竞态条件覆盖）
    const loadBoxInfos = () => {
      const selectedIds = new Set(coupon.value.box_info_ids)
      const selectedItems = boxInfoOptions.value.filter(item => selectedIds.has(item.id))

      store
        .dispatch(Actions.GET_LOTTERY_COUPON_BOX_INFOS)
        .then(() => {
          const results = store.getters.currentLotteryCouponBoxInfos
          // 合并结果和已选项目（去重）
          const resultIds = new Set(results.map((item: { id: number }) => item.id))
          const mergedOptions = [...results]
          selectedItems.forEach(item => {
            if (!resultIds.has(item.id)) {
              mergedOptions.push(item)
            }
          })
          boxInfoOptions.value = mergedOptions
        })
        .catch(() => {
          // ignore
        })
    }

    // 加载优惠券详情（编辑模式）
    if (!isNew) {
      store
        .dispatch(Actions.GET_LOTTERY_COUPON, route.params.id)
        .then(() => {
          const data = store.getters.currentLotteryCoupon
          coupon.value = {
            name: data.name,
            coupon_type: data.coupon_type,
            discount_value: data.discount_value,
            min_amount: data.min_amount,
            max_discount: data.max_discount,
            box_info_ids: data.box_info_ids || [],
            allowed_times: data.allowed_times || [],
            total_quantity: data.total_quantity,
            per_user_limit: data.per_user_limit,
            start_time: data.start_time,
            end_time: data.end_time,
            validity_days: data.validity_days,
            status: data.status,
            allow_user_claim: data.allow_user_claim ?? true
          }

          // 合并已选套系到选项列表（确保编辑时能显示名称）
          if (data.box_infos && data.box_infos.length > 0) {
            const existingIds = new Set(boxInfoOptions.value.map(item => item.id))
            data.box_infos.forEach((bi: { id: number; name: string }) => {
              if (!existingIds.has(bi.id)) {
                boxInfoOptions.value.push(bi)
              }
            })
          }

          // 判断有效期类型
          if (data.start_time && data.end_time) {
            validityType.value = 'range'
            timeRange.value = [data.start_time, data.end_time]
          } else if (data.validity_days) {
            validityType.value = 'days'
          } else {
            validityType.value = 'forever'
          }
        })
        .catch(() => {
          ElMessage.error('获取优惠券详情失败')
        })
    }

    const onSubmit = (formEl: FormInstance | undefined) => {
      if (!formEl) return

      formEl.validate((valid) => {
        if (valid) {
          const params = {
            ...coupon.value,
            total_quantity: coupon.value.total_quantity || null,
            per_user_limit: coupon.value.per_user_limit || null,
            max_discount: coupon.value.max_discount || null,
            validity_days: validityType.value === 'days' ? coupon.value.validity_days : null
          }

          if (isNew) {
            store
              .dispatch(Actions.CREATE_LOTTERY_COUPON, params)
              .then(() => {
                ElMessage.success('创建成功')
                router.push({ name: 'lottery_coupons' })
              })
              .catch(() => {
                ElMessage.error('创建失败')
              })
          } else {
            store
              .dispatch(Actions.UPDATE_LOTTERY_COUPON, {
                id: route.params.id,
                values: params
              })
              .then(() => {
                ElMessage.success('更新成功')
                router.push({ name: 'lottery_coupons' })
              })
              .catch(() => {
                ElMessage.error('更新失败')
              })
          }
        }
      })
    }

    const onCancel = () => {
      router.push({ name: 'lottery_coupons' })
    }

    return {
      formRef,
      coupon,
      rules,
      isNew,
      validityType,
      timeRange,
      boxInfoOptions,
      boxInfoLoading,
      searchBoxInfos,
      onSubmit,
      onCancel
    }
  }
})
